// Tells the native widgets whether Premium is active, so the gated ones
// (Planner, Clock-In · Donut, Habit Tracker) can show their locked face — see
// WidgetLock.java. Habits and Planner · Month are free and ignore this.
//
// Why it doesn't reuse usePremium(): that hook has its own copy of the auth
// state, so on every cold start it briefly reports "not premium" before the
// sign-in resolves — which would lock a paying user's widgets for a moment.
// This one waits for auth, then listens to the billing doc itself.
//
// Fails toward unlocked: a listener error sends nothing (the phone keeps its
// last answer), and Firebase being unconfigured sends nothing at all (the
// native side treats "never told" as unlocked).
import { useEffect } from 'react';
import { registerPlugin, Capacitor } from '@capacitor/core';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, isFirebaseConfigured } from '../firebase/config';
import { useAuth } from '../firebase/auth';
import { widgetPremiumPayload } from './widgetPremium';

const WidgetBridge = registerPlugin('WidgetBridge');
const isNative = Capacitor.isNativePlatform();

function send(payload) {
  WidgetBridge.setData({ widget: 'premium', json: JSON.stringify(payload) }).catch(() => {});
}

export function useWidgetPremium() {
  const { user, loading } = useAuth();
  const uid = user?.uid;
  useEffect(() => {
    if (!isNative || !isFirebaseConfigured || loading) return undefined;
    if (!uid) {
      send(widgetPremiumPayload(null));
      return undefined;
    }
    return onSnapshot(
      doc(db, 'users', uid, 'billing', 'status'),
      (snap) => send(widgetPremiumPayload(snap.data())),
      () => { /* keep whatever the phone already has */ },
    );
  }, [uid, loading]);
}
